import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie-player';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Spinner from 'src/components/ui/Features/Spinner/Spinner';
import { IFrame } from 'src/components/ui/GlobalComponents/IFrame/IFrame';
import { useLearning } from 'src/hook/data/useLearning';
import { useLesson } from 'src/hook/data/useLesson';
import { useScenario } from 'src/hook/data/useScenario';
import useUser from 'src/hook/data/useUser';
import { useGlobalMessageListener } from 'src/hook/global/useGlobalMessageListener';
import { useComponentModal } from 'src/hook/modal/useComponentModal';
import usePopUp from 'src/hook/modal/usePopUp';
import { AnimationType, IFrameMessage, Pages } from 'src/model/enum/enums';
import { Chl, Ctx } from 'src/model/Scenario';
import { Word } from 'src/model/Word';
import { GetottieUrl, GetPeopleImageUrl, GetRazorSiteUrl, IconLocation, ImageLocation, ScenariosLocation } from 'src/utils/URLUtils';
import styles from './ScenarioChat.module.scss';

const ScenarioChat = () => {
    const { scenarios, isLoading } = useLesson();
    const [searchParams] = useSearchParams();
    const ref = searchParams.get('id');
    const index = searchParams.get('index');
    const { learningInfo } = useLearning();
    const { open } = useComponentModal();
    const navigate = useNavigate();
    const [currentChl, setCurrentChl] = useState<Chl | undefined>(undefined);
    const [currentContext, setCurrentContext] = useState<Ctx | undefined>(undefined);
    const [showGuide, setShowGuide] = useState(false);
    const { userInfo } = useUser();
    const [showPrize, setShowPrize] = useState(false);
    const { addChallenge, getRandomWords, scenarioRandomWords, setScenarioRandomWords } = useScenario();
    const { t } = useTranslation();
    const { showClosePopUp } = usePopUp();

    const handleWordClick = (word: Word) => {
        open(Pages.Definition, { word: word }, AnimationType.SlideUp);
    }

    const handleCloseModal = () => {
        navigate(`/scenario/category?id=${ref}`);
    }

    useEffect(() => {
        if (!scenarios || !scenarios?.categories || !ref || !index) return;

        const categoryRef = ref.substring(0, 2);
        const contextRef = ref.substring(2, 4);
        const category = scenarios?.categories.find((cat: any) => cat.ref === categoryRef);
        if (category) {
            const context = category.ctx.find((ctx: any) => ctx.ref === contextRef);
            const chl = context?.chl.find((chl: any) => chl.ref === index);
            const wordsList = chl?.w;
            setCurrentChl(chl);
            setCurrentContext(context);
            if (wordsList && !scenarioRandomWords) {
                // Get random words and update Redux state
                getRandomWords(wordsList, true);
            }
        }
    }, [scenarios, ref, index, getRandomWords, scenarioRandomWords]);

    const handleMessage = useCallback((event: MessageEvent) => {
        if (event.data.type === IFrameMessage.UserMessage && scenarioRandomWords) {
            const message = event.data.message.toLowerCase();

            // Find words that should be removed
            const wordsToRemove = scenarioRandomWords.filter(word =>
                message.includes(word.Text.toLowerCase())
            );

            // Animate words being removed
            wordsToRemove.forEach(word => {
                const wordElement = document.getElementById(`word-${word.ID}`);
                if (wordElement) {
                    wordElement.classList.add(styles.wordRemoved);
                }
            });

            // Remove words after animation
            setTimeout(() => {
                const newWords = scenarioRandomWords.filter(word =>
                    !message.includes(word.Text.toLowerCase())
                );

                // Update Redux state with the filtered words
                setScenarioRandomWords(newWords);

                // Show prize if this was the last word
                if (newWords.length === 0 && scenarioRandomWords.length > 0) {
                    setShowPrize(true);
                    addChallenge(ref!, index!, currentContext?.chl || []);
                }
            }, 500);
        }
    }, [scenarioRandomWords, ref, index, currentContext?.chl, addChallenge, setScenarioRandomWords]);


    useGlobalMessageListener(handleMessage, [IFrameMessage.UserMessage]);

    if (isLoading || !scenarios) {
        return <Spinner />;
    }

    if (!ref || !scenarioRandomWords || !currentChl) {
        return <div>{t('Scenario not found')}</div>;
    }

    return (
        <div className={styles.scenarioChat}>
            <div
                className={`${styles.backdrop} ${showGuide ? styles.visible : ''}`}
                onClick={() => setShowGuide(false)}
            />
            <div className={styles.questionImageContainer}>
                <div style={{ backgroundImage: `url(${ScenariosLocation(ref)})` }}
                    className={styles.questionImage}
                    onClick={() => setShowGuide(false)}
                >
                    <img
                        src={GetPeopleImageUrl(currentChl.n)}
                        alt={currentChl.n}
                        className={styles.character}
                    />
                    <span
                        onClick={() => showClosePopUp(handleCloseModal)}
                        className={`${styles.icon} ${styles.right}`}>
                        <img src={IconLocation('x')} alt="close" />
                    </span>
                    <span onClick={(e) => {
                        e.stopPropagation();
                        setShowGuide(!showGuide);
                    }}
                        className={`${styles.icon} ${styles.left}`}>
                        <img src={IconLocation('question')} alt="guide" />
                    </span>

                    <div
                        className={`${styles.guideOverlay} ${showGuide ? styles.visible : ''}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img src={ImageLocation('Lexi/smile.png')} alt="challenge" />
                        <p>{t('Challenge: Use these words correctly in the conversation.')}</p>
                    </div>
                </div>
            </div>
            {!showPrize && <div className={styles.wordsList}>
                {scenarioRandomWords.map((word, index) => (
                    <span
                        onClick={() => handleWordClick(word)}
                        key={word.ID}
                        id={`word-${word.ID}`}
                        className={`${styles.wordItem} ${learningInfo?.KnownWordIds.includes(word.ID)
                            ? styles.knownWord
                            : learningInfo?.ToLearn.includes(word.ID)
                                ? styles.ToLearnWord
                                : styles.unknownWord
                            }`}
                    >
                        {word.Text}
                    </span>
                ))}
            </div>}

            <div className={styles.iframeContainer}>
                <IFrame src={GetRazorSiteUrl(`practice/challenge-chat`,
                    userInfo?.Token, `&ref=${ref}${index}`)}
                    title="General Chat" />

                {showPrize &&
                    <div className={styles.continueButtonContainer}>
                        <button className={styles.continueButton} onClick={() =>
                            navigate(`/scenario`)}>
                            Continue</button>
                    </div>}
            </div>

            {showPrize && <div className={styles.lottieContainer}>
                <Lottie
                    path={GetottieUrl(`Celebrate/${Math.random() < 0.5 ? '4' : '5'}`)}
                    play
                    className={styles.lottie}
                />
            </div>}

        </div>
    );
};

export default ScenarioChat;
